import workspace from "../../workspace";
import {findLowestRightmostPoint} from "../../../../../helpers/coordinatesHelper";
import {canShapeBeAutoShifted, getLineDirection} from "../../../../../helpers/shapeHelper";
import sqftShapeManager from "../../../store/sqftShapeManager";
import {getPointDistance} from "../../../../../helpers/sqftHelper";
import {isHorizontalDirection, isVerticalDirection} from "../../../helpers/lineHelper";
import workspaceControllers from "../../../store/workspaceControllers";
import sqftShapeIntersectionsManager from "../../../store/sqftShapeIntersectionsManager";
import sqftShape from "../sqftShape";

export default class ShapeAutoShuffle {
  __shape;

  constructor(shape) {
    this.__shape = shape;
  }

  handlePlaceShapeOnTheLine = () => {
    // Auto Shuffle
    const hoveredLines = workspace.getHoveredLines([this.__shape.id]);

    if (hoveredLines.length > 0) {
      // Get relevant shapes and lines
      const hoveredLine = this.getHoveredLine(hoveredLines);
      const hoveredShape = hoveredLine.getParent();

      if (canShapeBeAutoShifted(this.__shape, hoveredShape)) {
        const newPosition = this.calculateNewPosition(hoveredLine, hoveredShape);

        if (newPosition) {
          // Check if there's an adjacent shape (left for vertical, above for horizontal)
          const hasAdjacentShape = this.checkForAdjacentShape(hoveredShape, newPosition, hoveredLine);

          if (hasAdjacentShape) {
            // Original behavior - shift all shapes
            this.performFullAutoShuffle(newPosition, hoveredLine, hoveredShape);
          } else {
            // New behavior - just align the current shape
            this.alignShapeToExistingShapes(newPosition, hoveredLine, hoveredShape);
          }

          this.__shape.resetState();
          sqftShapeIntersectionsManager.updateShapesIntersections();
          workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(false);
          workspace.getHoveredLines(); // update hovered lines
          return true;
        }
      }
    }
  }

  /**
   * Check if there's a shape adjacent to the position (left for vertical lines, above for horizontal lines)
   * @param {Object} hoveredShape - The shape being hovered
   * @param {Object} position - The position to check
   * @param {Object} hoveredLine - The line being hovered
   * @returns {boolean} - True if there's an adjacent shape
   */
  checkForAdjacentShape(hoveredShape, position, hoveredLine) {
    const snappedShapeGroup = hoveredShape.shapeAbsolute.getSnappedShapeGroup();
    let shapesToCheck = [];

    if (snappedShapeGroup !== null && snappedShapeGroup !== undefined) {
      const snappedShapes = workspaceControllers.snappedShapeGroups.getSnappedShapes(snappedShapeGroup);
      if (snappedShapes) {
        shapesToCheck = snappedShapes.filter(shape =>
          shape.id !== this.__shape.id &&
          (hoveredShape.partId === shape.partId ||
            hoveredShape.partId === shape.parentPartId ||
            (hoveredShape.parentPartId && hoveredShape.parentPartId === shape.partId))
        );
      }
    } else {
      shapesToCheck = []
    }

    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);
    const threshold = 5; // pixels tolerance

    if (isVerticalDirection(hoveredLineDirection)) {
      // Check for shapes to the left
      return shapesToCheck.some(shape => {
        const shapeMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
        const shapeRightX = shapeMeasurementPoints[1].x;
        const shapeTopY = shapeMeasurementPoints[0].y;
        const shapeBottomY = shapeMeasurementPoints[3].y;

        // Check if shape is to the left and aligned vertically
        return Math.abs(shapeRightX - position.x) < threshold &&
          position.y >= shapeTopY - threshold &&
          position.y <= shapeBottomY + threshold;
      });
    } else if (isHorizontalDirection(hoveredLineDirection)) {
      // Check for shapes above
      return shapesToCheck.some(shape => {
        const shapeMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
        const shapeBottomY = shapeMeasurementPoints[3].y;
        const shapeLeftX = shapeMeasurementPoints[0].x;
        const shapeRightX = shapeMeasurementPoints[1].x;

        // Check if shape is above and aligned horizontally
        return Math.abs(shapeBottomY - position.y) < threshold &&
          position.x >= shapeLeftX - threshold &&
          position.x <= shapeRightX + threshold;
      });
    }

    return false;
  }

  /**
   * Perform the original auto-shuffle behavior
   */
  performFullAutoShuffle(newPosition, hoveredLine, hoveredShape) {
    // Move shape to new positions
    this.moveShapeToPositionAndRotate(newPosition, hoveredLine);

    // Set same snapped shape group before get shapes to shift
    workspaceControllers.snappedShapeGroups.setSameSnappedShapesGroup(this.__shape, hoveredShape);

    // Get shapes to shift
    const shapesToShift = this.getShapesToShift(hoveredShape, newPosition);

    // Calculate new positions
    const [xDiff, yDiff] = this.calculatePositionDiffForOtherShapes(hoveredLine);
    this.moveShapesToPositions(shapesToShift, xDiff, yDiff);
  }

  /**
   * Just align the current shape to existing shapes without shifting others
   */
  alignShapeToExistingShapes(newPosition, hoveredLine, hoveredShape) {
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    // Get the measurement points of the hovered shape
    const hoveredShapeMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([hoveredShape]);

    // Calculate aligned position based on line direction
    let alignedPosition = {...newPosition};

    // Get current shape dimensions
    const currentShapeWidth = this.__shape.getWidth();
    const currentShapeDepth = this.__shape.getDepth();

    if (isVerticalDirection(hoveredLineDirection)) {
      // For vertical line - position shape to the left of the hovered shape
      const hoveredLinePointFromPosOnWorkspace = hoveredLine.from.getPointPosOnWorkspace();

      // Check which edge of the shape we're on
      if (hoveredShapeMeasurementPoints[0].x === hoveredLinePointFromPosOnWorkspace.x) {
        // We're on the left edge - place our shape to the left
        alignedPosition.x = hoveredShapeMeasurementPoints[0].x - currentShapeWidth;
      } else if (hoveredShapeMeasurementPoints[1].x === hoveredLinePointFromPosOnWorkspace.x) {
        // We're on the right edge - place our shape to the right
        alignedPosition.x = hoveredShapeMeasurementPoints[1].x;
      }

      // Keep Y position aligned with the hovered shape
      alignedPosition.y = hoveredShapeMeasurementPoints[0].y;

    } else if (isHorizontalDirection(hoveredLineDirection)) {
      // For horizontal line - position shape above or below the hovered shape
      const hoveredLinePointFromPosOnWorkspace = hoveredLine.from.getPointPosOnWorkspace();

      // Check which edge of the shape we're on
      if (hoveredShapeMeasurementPoints[0].y === hoveredLinePointFromPosOnWorkspace.y) {
        // We're on the top edge - place our shape above
        alignedPosition.y = hoveredShapeMeasurementPoints[0].y - currentShapeDepth;
      } else if (hoveredShapeMeasurementPoints[3].y === hoveredLinePointFromPosOnWorkspace.y) {
        // We're on the bottom edge - place our shape below
        alignedPosition.y = hoveredShapeMeasurementPoints[3].y;
      }

      // Keep X position aligned with the hovered shape
      alignedPosition.x = hoveredShapeMeasurementPoints[0].x;
    }

    // Move and rotate the shape first
    this.moveShapeToPositionAndRotate(alignedPosition, hoveredLine);

    // After rotation, we might need to adjust position again if shape was rotated
    // because rotation changes the effective width/depth
    if (this.__shape.rotatedDegrees % 180 !== 0) {
      // Shape is rotated 90 or 270 degrees - width and depth are swapped
      const adjustedPosition = {...alignedPosition};

      if (isVerticalDirection(hoveredLineDirection)) {
        // Recalculate with swapped dimensions after rotation
        const rotatedWidth = currentShapeDepth; // swapped due to rotation

        if (hoveredShapeMeasurementPoints[0].x === hoveredLine.from.getPointPosOnWorkspace().x) {
          adjustedPosition.x = hoveredShapeMeasurementPoints[0].x - rotatedWidth;
        }
      }

      // Apply adjusted position
      this.__shape.setPosition(adjustedPosition);
      this.__shape.shapeAbsolute.setAbsolutePos(adjustedPosition);
    }

    // Set same snapped shape group
    workspaceControllers.snappedShapeGroups.setSameSnappedShapesGroup(this.__shape, hoveredShape);
  }

  /**
   * Will return line for the lowest and rightmost shape
   * @param hoveredLines
   */
  getHoveredLine = (hoveredLines) => {
    const hoveredLinesShapes = hoveredLines.map(line => line.getParent());
    const lowestRightmostPoint = findLowestRightmostPoint(hoveredLinesShapes.map(shape => shape.getPosition()));
    const shapeToShift = hoveredLinesShapes.find(shape => {
      return shape.getPosition() === lowestRightmostPoint
        && shape.id !== this.__shape.id // skip current moving shape
    });

    return hoveredLines.find(line => line.getParent().id === shapeToShift.id);
  }

  /**
   * Will return all shapes that are lower or on the same line then point
   * @param hoveredShape
   * @param point
   */
  getShapesToShift = (hoveredShape, point) => {
    const snappedShapeGroup = hoveredShape.shapeAbsolute.getSnappedShapeGroup();
    if (snappedShapeGroup !== null && snappedShapeGroup !== undefined) {
      const snappedShapes = workspaceControllers.snappedShapeGroups.getSnappedShapes(snappedShapeGroup);
      if (snappedShapes) {
        const snappedShapesWithoutMovingShape = snappedShapes.filter(shape =>
          shape.id !== this.__shape.id
          && (hoveredShape.partId === shape.partId
            || hoveredShape.partId === shape.parentPartId
            || (hoveredShape.parentPartId && hoveredShape.parentPartId === shape.partId) // add spacers
          )
        )
        return this.findShapesRightAndBelow(snappedShapesWithoutMovingShape, point);
      } else {
        return this.findShapesRightAndBelow(hoveredShape, point);
      }
    }
    return []
  }

  /**
   * Calculate new position based on hovered line
   */
  calculateNewPosition(hoveredLine, shapeToShift) {
    const shapeToShiftMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shapeToShift]);
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    let newShapePosition = null;
    const hoveredLinePointFromPosOnWorkspace = hoveredLine.from.getPointPosOnWorkspace();

    if (isVerticalDirection(hoveredLineDirection)) {
      // Check whether it is shape start or end
      if (shapeToShiftMeasurementPoints[1].x === hoveredLinePointFromPosOnWorkspace.x) {
        newShapePosition = shapeToShiftMeasurementPoints[1];
        // Shift shape only if it is first line
      } else if (shapeToShiftMeasurementPoints[0].x === hoveredLinePointFromPosOnWorkspace.x) {
        newShapePosition = shapeToShiftMeasurementPoints[0];
      }
    } else if (isHorizontalDirection(hoveredLineDirection)) {
      // Check whether it is shape start or end
      if (shapeToShiftMeasurementPoints[0].y === hoveredLinePointFromPosOnWorkspace.y) {
        newShapePosition = shapeToShiftMeasurementPoints[0];
        // Shift shape only if it is first line
      } else if (shapeToShiftMeasurementPoints[3].y === hoveredLinePointFromPosOnWorkspace.y) {
        newShapePosition = shapeToShiftMeasurementPoints[3];
      }
    }

    return newShapePosition
  }

  /**
   * Calculate position difference for other shapes
   */
  calculatePositionDiffForOtherShapes(hoveredLine) {
    let xDiff = 0;
    let yDiff = 0;
    const thisShapeMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([this.__shape]);
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    if (isVerticalDirection(hoveredLineDirection)) {
      xDiff = getPointDistance(thisShapeMeasurementPoints[0], thisShapeMeasurementPoints[1]);
    } else if (isHorizontalDirection(hoveredLineDirection)) {
      yDiff = getPointDistance(thisShapeMeasurementPoints[3], thisShapeMeasurementPoints[0]);
    }

    return [xDiff, yDiff]
  }

  /**
   * Move shapes to new positions
   */
  moveShapesToPositions(shapesToShift, xDiff, yDiff) {
    shapesToShift.forEach(shape => {
      const shapeCurrentPos = shape.getPosition();
      const shapeNewPos = {x: shapeCurrentPos.x + xDiff, y: shapeCurrentPos.y + yDiff};
      shape.setPosition(shapeNewPos);
      shape.shapeAbsolute.setAbsolutePos(shapeNewPos);
    })
  }

  /**
   * Moves and rotates the shape based on the line's orientation and the current rotation angle
   */
  moveShapeToPositionAndRotate(newPosition, hoveredLine) {
    // Set the new position
    this.__shape.setPosition(newPosition);
    this.__shape.shapeAbsolute.setAbsolutePos(newPosition);

    // Determine the required rotation based on the line direction
    const hoveredLineDirection = getLineDirection(hoveredLine.from, hoveredLine.to);

    const isHorizontal = isHorizontalDirection(hoveredLineDirection);
    const isVertical = isVerticalDirection(hoveredLineDirection);
    const hoveredShape = hoveredLine.getParent();

    // Rotate by -90 degrees (right) if the current angle is 90 and the line is vertical
    if (hoveredShape.rotatedDegrees === 270 && isHorizontal) {
      let desiredAngle = 270;
      let normalizedCurrentAngle = ((this.__shape.rotatedDegrees % 360) + 360) % 360;
      let angleDifference = desiredAngle - normalizedCurrentAngle;
      this.__shape.rotate(angleDifference, this.__shape.Direction.LEFT, false);
    }
    // Rotate by 90 degrees if the current angle is 0 and the line is horizontal
    else if (this.__shape.rotatedDegrees === 0 && isHorizontal) {
      this.__shape.rotate(90, this.__shape.Direction.LEFT, false);
    }
    // Rotate by -90 degrees (right) if the current angle is 90 and the line is vertical
    else if (this.__shape.rotatedDegrees === 90 && isVertical) {
      this.__shape.rotate(-90, this.__shape.Direction.LEFT, false);
    }

    // Calculate the new coordinates for the upper left corner after potential rotation
    if (isHorizontalDirection(hoveredLineDirection)) {
      // fix shape if position is in the top left corner
      // Calculate shift is not needed if the center remains in place
      const cos = Math.cos(this.__shape.rotatedDegrees * Math.PI / 180);
      const sin = Math.sin(this.__shape.rotatedDegrees * Math.PI / 180);
      const originalWidth = this.__shape instanceof sqftShape ? this.__shape.getWidth() : this.__shape.getWidth();
      const originalDepth = this.__shape instanceof sqftShape ? this.__shape.getDepth() : this.__shape.getDepth();

      // Calculate the center coordinates
      const centerX = newPosition.x + originalDepth / 2;
      const centerY = newPosition.y + originalWidth / 2;

      // Coordinates of the upper left corner after rotation
      const upperLeftX = centerX - (originalDepth / 2 * cos + originalWidth / 2 * sin);
      const upperLeftY = centerY - (originalDepth / 2 * sin - originalWidth / 2 * cos);

      // Set the new coordinates for the upper left corner
      this.__shape.setPosition({
        x: upperLeftX,
        y: upperLeftY
      });
      this.__shape.shapeAbsolute.setAbsolutePos({
        x: upperLeftX,
        y: upperLeftY
      });
    }
  }

  /**
   * Function for finding all shapes that are to the right and below a point
   * @param {Array} shapes
   * @param referencePosition
   * @returns {Array}
   */
  findShapesRightAndBelow(shapes, referencePosition) {
    const resultShapes = [];

    shapes.forEach(shape => {
      const shapeMeasurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace([shape]);
      const shapeTopLeftPoint = shapeMeasurementPoints[0];

      if (shapeTopLeftPoint.x >= referencePosition.x && shapeTopLeftPoint.y >= referencePosition.y) {
        resultShapes.push(shape);
      }
    });

    return resultShapes;
  }
}
